import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  PHASE_OF_PLAY_META,
  GRASSROOTS_PHASE_META,
  phasesForTier,
  phaseMetaForTier,
  projectToGrassrootsPhase,
  tierForCoachLevel,
} from '@4cm/core/phaseOfPlay';
import type { PhaseOfPlay, GrassrootsPhase } from '@4cm/core/phaseOfPlay';
import { BrainOrb, Button, Card, ChipStrip, EmptyState, GlassPill, Skeleton } from '../components/primitives';
import { useDrillLibrary, type LibraryDrill } from '../lib/queries/useDrillLibrary';
import { useCalibration } from '../contexts/CalibrationContext';
import { DrillMiniPreview } from '../components/drillCanvas/DrillMiniPreview';
import { synthesizeDrillSpec } from '../components/drillCanvas/synthesizeDrillSpec';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import './page-shell.css';
import './Drills.css';

// Reverse mapping for the grassroots → academy expansion. Keyed by
// grassroots phase, returns the academy phases that fold into it.
// Mirrors ACADEMY_TO_GRASSROOTS in packages/core/src/phaseOfPlay.ts —
// kept inline here to avoid exposing the private constant.
const GRASSROOTS_TO_ACADEMY_PHASES: Record<GrassrootsPhase, PhaseOfPlay[]> = {
  in_possession: ['attacking', 'individual'],
  out_of_possession: ['defending'],
  transition: ['transitions'],
};

/** Short human-readable "time since" — "2m ago", "5d ago", "Mar 4". */
function relativeTime(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const secs = Math.max(0, (Date.now() - t) / 1000);
  if (secs < 60) return 'just now';
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  if (secs < 604800) return `${Math.floor(secs / 86400)}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const fieldGroupStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 12,
};
const fieldLabelStyle: React.CSSProperties = {
  opacity: 0.65,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  fontSize: 10,
};
const selectStyle: React.CSSProperties = {
  padding: '6px 10px',
  background: 'rgba(255,255,255,0.06)',
  color: 'inherit',
  border: '1px solid rgba(255,255,255,0.18)',
  borderRadius: 6,
  fontSize: 12,
};

export function Drills() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [creating, setCreating] = useState(false);
  const { coachLevel, level } = useCalibration();
  const tier = tierForCoachLevel(coachLevel);
  // Authoring + refining drills is an academy-level affordance — novice
  // coaches consume the library, they don't curate it. Per AGENTS.md
  // "no disabled buttons; hide unavailable features".
  const showAuthoring = level === 'academy';

  // The active filter is stored in the tier's vocabulary. At grassroots
  // it's a GrassrootsPhase ('in_possession' | 'out_of_possession' |
  // 'transition'); at academy it's a PhaseOfPlay. Storage is academy
  // 4-phase; we expand here when querying.
  const [activePhase, setActivePhase] = useState<string | null>(null);
  const [curatedOnly, setCuratedOnly] = useState(false);
  // Search + author + sort. Sort defaults to "recent" so the coach's
  // last edit appears at the top — the most common reason to visit
  // the library is "where was that drill I just saved?".
  const [search, setSearch] = useState('');
  const [authorFilter, setAuthorFilter] = useState<'all' | 'me' | 'brain' | 'library' | 'coaches'>('all');
  const [sortMode, setSortMode] = useState<'recent' | 'name'>('recent');
  // Optimistic delete: rows soft-deleted in this session are hidden
  // immediately, no refetch needed.
  const [deletedIds, setDeletedIds] = useState<Set<string>>(new Set());
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  // Inline rename — optimistic name override keyed by id so the card
  // title updates immediately, no refetch needed.
  const [renameTarget, setRenameTarget] = useState<{ id: string; name: string } | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [renamed, setRenamed] = useState<Record<string, string>>({});
  // Duplicate — id currently being copied (drives the per-card spinner).
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);

  const tierPhases = useMemo(() => phasesForTier(tier), [tier]);

  // Translate the selected tier phase into the academy phases the
  // useDrillLibrary query needs. Grassroots picks fan out; academy
  // picks pass through 1:1.
  const queryPhases = useMemo<PhaseOfPlay[] | null>(() => {
    if (!activePhase) return null;
    if (tier === 'grassroots') {
      return GRASSROOTS_TO_ACADEMY_PHASES[activePhase as GrassrootsPhase] ?? null;
    }
    return [activePhase as PhaseOfPlay];
  }, [activePhase, tier]);

  const { drills: allDrills, state: drillsState, source: drillsSource } = useDrillLibrary({
    phases: queryPhases,
    curatedOnly,
    sort: sortMode,
  });

  // Apply search + author filter client-side. Search matches name +
  // description case-insensitively. Author filter buckets by source +
  // created_by — "Me" matches the signed-in coach's CLUB drills.
  const drills = useMemo(() => {
    if (!allDrills) return null;
    const q = search.trim().toLowerCase();
    return allDrills.filter((d) => {
      if (deletedIds.has(d.id)) return false;
      if (q) {
        const hay = `${d.name ?? ''} ${d.description ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      switch (authorFilter) {
        case 'all':
          return true;
        case 'me':
          return d.source_type === 'CLUB' && d.created_by === user?.id;
        case 'brain':
          return d.source_type === 'AI_GENERATED';
        case 'library':
          return d.source_type === 'PLATFORM' || d.source_type === 'LICENSED' || d.source_type === 'CURATED_EXPERT';
        case 'coaches':
          return d.source_type === 'CLUB' && d.created_by !== user?.id;
        default:
          return true;
      }
    });
  }, [allDrills, search, authorFilter, user?.id, deletedIds]);

  // Rename: persist the new name, then optimistically override the card
  // title via the `renamed` map. Library/curated drills aren't renamable
  // (the action is only offered on CLUB + Brain drafts).
  async function submitRename() {
    if (!renameTarget || renaming) return;
    const next = renameValue.trim();
    if (!next) return;
    setRenaming(true);
    const { error } = await supabase
      .from('drills')
      .update({ name: next })
      .eq('id', renameTarget.id);
    setRenaming(false);
    if (error) {
      alert(`Couldn't rename drill: ${error.message}`);
      return;
    }
    setRenamed((prev) => ({ ...prev, [renameTarget.id]: next }));
    setRenameTarget(null);
  }

  // Duplicate: deep-copy the source row into a new coach-owned CLUB drill,
  // then open the copy so it can be renamed/tweaked straight away. We copy
  // the full row (corner tags, progressions, equipment, canvas spec…) — the
  // library card only carries a subset — and reset provenance so the copy is
  // the coach's own, never carrying platform / AI / curator attribution.
  async function handleDuplicate(d: LibraryDrill) {
    if (!user || duplicatingId) return;
    setDuplicatingId(d.id);
    try {
      const { data: src, error: fetchErr } = await supabase
        .from('drills')
        .select('*')
        .eq('id', d.id)
        .maybeSingle();
      if (fetchErr || !src) {
        alert(`Couldn't duplicate drill: ${fetchErr?.message ?? 'drill not found'}`);
        return;
      }
      // Resolve the coach's org so the copy is scoped to them.
      const { data: u } = await supabase
        .from('users')
        .select('org_id')
        .eq('id', user.id)
        .maybeSingle();
      const orgId = (u as { org_id?: string } | null)?.org_id ?? null;
      const sourceName = (src as { name?: string | null }).name ?? 'Untitled drill';
      // Strip server-managed columns — the copy gets a fresh id + timestamps.
      const content: Record<string, unknown> = { ...(src as Record<string, unknown>) };
      delete content.id;
      delete content.created_at;
      delete content.updated_at;
      delete content.deleted_at;
      const { data: inserted, error: insErr } = await supabase
        .from('drills')
        .insert({
          ...content,
          name: `${sourceName} (copy)`,
          source_type: 'CLUB',
          is_custom: true,
          curator_name: null,
          curator_credentials: null,
          license_id: null,
          org_id: orgId,
          created_by: user.id,
        })
        .select('id')
        .single();
      if (insErr || !inserted) {
        alert(`Couldn't duplicate drill: ${insErr?.message ?? 'unknown error'}`);
        return;
      }
      navigate(`/drills/${(inserted as { id: string }).id}`);
    } finally {
      setDuplicatingId(null);
    }
  }

  return (
    <main className="main page drills-page">
      <header className="page-head drills-head">
        <BrainOrb size="lg" ariaLabel="Brain" />
        <div style={{ flex: 1 }}>
          <span className="page-eyebrow">
            Library · {drillsState === 'loading' ? '…' : `${drills?.length ?? 0} drills`}
          </span>
          <h1 className="page-title">Drills</h1>
          <p className="page-sub">Filter by phase. Pick what fits the night.</p>
        </div>
        {/* Two creation paths — manual + Brain-assisted. Mirrors the
            "+ Plan new session" pattern on /sessions but offers an
            AI-driven entry alongside the blank canvas one. */}
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            type="button"
            variant="glass"
            size="touchline"
            onClick={() => {
              window.dispatchEvent(
                new CustomEvent('brain-chat:open', {
                  detail: { seed: 'Draft me a new drill. Ask me what phase, age group, theme and how many players first if you need to.' },
                }),
              );
            }}
          >
            ✨ Ask Brain
          </Button>
          <Button
            type="button"
            variant="primary"
            size="touchline"
            disabled={creating || !user}
            onClick={async () => {
              if (!user) return;
              setCreating(true);
              // Resolve the coach's org so the new drill is scoped right.
              const { data: u } = await supabase
                .from('users')
                .select('org_id')
                .eq('id', user.id)
                .maybeSingle();
              const orgId = (u as { org_id?: string } | null)?.org_id ?? null;
              const blankSpec = {
                schemaVersion: 1,
                customPitch: { width: 30, length: 40 },
                entities: [],
                cones: [],
                arrows: [],
                zones: [],
                texts: [],
                keyframes: [],
              };
              const { data, error } = await supabase
                .from('drills')
                .insert({
                  org_id: orgId,
                  name: 'New drill',
                  description: '',
                  duration_mins: 15,
                  drill_type: 'TECHNICAL',
                  is_custom: true,
                  source_type: 'CLUB',
                  difficulty: 'INTERMEDIATE',
                  coaching_points: [],
                  drill_spec: blankSpec,
                  created_by: user.id,
                })
                .select('id')
                .single();
              setCreating(false);
              if (error || !data) {
                alert(`Couldn't create drill: ${error?.message ?? 'unknown error'}`);
                return;
              }
              // Land on Josh's drill canvas (TacticsBoard 2D/3D + Brain) —
              // primary authoring surface for v2026-05+.
              navigate(`/drills/${(data as { id: string }).id}/canvas`);
            }}
            className="drills-author-cta"
          >
            {creating ? 'Creating…' : '+ New drill'}
          </Button>
        </div>
      </header>

      <section className="drills-filters" aria-label="Filters">
        <ChipStrip
          chips={[
            { label: 'all phases', value: '__all', active: activePhase === null },
            ...tierPhases.map((p) => {
              const meta = phaseMetaForTier(p, tier);
              return {
                label: meta?.label ?? p,
                value: p,
                active: activePhase === p,
              };
            }),
          ]}
          onSelect={(value) => setActivePhase(value === '__all' ? null : value)}
        />
        <button
          type="button"
          className="drills-curated-toggle"
          data-active={curatedOnly || undefined}
          onClick={() => setCuratedOnly((v) => !v)}
        >
          Coach&apos;s Corner only
        </button>
      </section>

      {/* Search + Author + Sort — keeps the library navigable as the
          coach's drill count grows. */}
      <section
        aria-label="Search and sort"
        style={{
          display: 'flex',
          gap: 12,
          alignItems: 'center',
          flexWrap: 'wrap',
          margin: '8px 0 16px',
          padding: '0 4px',
        }}
      >
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="🔍 Search name or description"
          aria-label="Search drills"
          style={{
            flex: '1 1 240px',
            minWidth: 200,
            padding: '8px 12px',
            background: 'rgba(255,255,255,0.06)',
            color: 'inherit',
            border: '1px solid rgba(255,255,255,0.18)',
            borderRadius: 8,
            fontSize: 13,
          }}
        />
        <label style={fieldGroupStyle}>
          <span style={fieldLabelStyle}>Authored by</span>
          <select
            value={authorFilter}
            onChange={(e) => setAuthorFilter(e.target.value as typeof authorFilter)}
            style={selectStyle}
          >
            <option value="all">Anyone</option>
            <option value="me">Me</option>
            <option value="coaches">Other coaches</option>
            <option value="brain">Brain (AI)</option>
            <option value="library">Library / curated</option>
          </select>
        </label>
        <label style={fieldGroupStyle}>
          <span style={fieldLabelStyle}>Sort</span>
          <select
            value={sortMode}
            onChange={(e) => setSortMode(e.target.value as typeof sortMode)}
            style={selectStyle}
          >
            <option value="recent">Recent</option>
            <option value="name">A–Z</option>
          </select>
        </label>
        {(search || authorFilter !== 'all') && (
          <button
            type="button"
            onClick={() => { setSearch(''); setAuthorFilter('all'); }}
            style={{
              padding: '8px 12px',
              background: 'transparent',
              color: 'inherit',
              border: '1px solid rgba(255,255,255,0.18)',
              borderRadius: 8,
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            Clear filters
          </button>
        )}
      </section>

      {drillsState === 'loading' && (
        <ul className="drills-list" aria-label="Loading drills">
          {Array.from({ length: 5 }, (_, i) => (
            <li key={i}>
              <Card variant="opaque">
                <Skeleton variant="text" width="55%" height={18} />
                <div style={{ height: 8 }} />
                <Skeleton variant="text" width="80%" height={14} />
                <div style={{ height: 8 }} />
                <Skeleton variant="pill" />
              </Card>
            </li>
          ))}
        </ul>
      )}
      {drillsSource === 'seed' && drillsState === 'ok' && (
        <p className="drills-seed-hint">
          Showing the offline starter library — connect a backend to see your full drill catalogue.
        </p>
      )}
      {drillsState === 'empty' && (
        <EmptyState
          glyph="🎯"
          title="No drills match"
          body="Drop the curated filter or try a different phase. Brain can also draft a drill for you."
          cta={{ label: 'Author with Brain', onClick: () => navigate('/drills/new') }}
          hint={<>or press <kbd>⌘K</kbd> and ask Brain</>}
        />
      )}

      {drills && drills.length > 0 && (
        <ul className="drills-list">
          {drills.map((d) => {
            const isCurated = d.source_type === 'CURATED_EXPERT';
            // Rename + delete are coach-owned affordances: only on the
            // coach's CLUB drills and Brain drafts, never the shared
            // library/curated catalogue. Duplicate is offered on every drill.
            const canEdit = d.source_type === 'CLUB' || d.source_type === 'AI_GENERATED';
            return (
              <li key={d.id} data-testid="drill-card" data-drill-id={d.id}>
                <Link to={`/drills/${d.id}`} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                  <Card variant="opaque">
                    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                      {/* Thumbnail — one unified preview (projects legacy
                          entities, renders zones/texts). Synthesise a sensible
                          layout when the drill has no spec so cards never
                          collapse to an empty pitch. */}
                      <div className="drills-card__thumb" style={{ flexShrink: 0, width: 180 }}>
                        <DrillMiniPreview
                          spec={d.drill_spec ?? synthesizeDrillSpec({
                            drillType: d.drill_type,
                            primaryPhase: d.primary_phase,
                            name: d.name,
                          })}
                          width={180}
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="drills-card__head">
                          <h2 className="drills-card__title">{renamed[d.id] ?? d.name ?? 'Untitled drill'}</h2>
                          <div className="drills-card__pills">
                            {d.primary_phase && (() => {
                              // Project the academy-tagged primary_phase into
                              // the active tier's vocabulary so a grassroots
                              // coach sees "In possession" not "Attacking".
                              const renderPhase = tier === 'grassroots'
                                ? projectToGrassrootsPhase(d.primary_phase)
                                : d.primary_phase;
                              const meta = tier === 'grassroots'
                                ? GRASSROOTS_PHASE_META[renderPhase as GrassrootsPhase]
                                : PHASE_OF_PLAY_META[renderPhase as PhaseOfPlay];
                              return (
                                <GlassPill mono>
                                  <span aria-hidden="true">{meta.glyph}</span>
                                  {meta.label}
                                </GlassPill>
                              );
                            })()}
                            {d.duration_mins && <GlassPill mono>{d.duration_mins} min</GlassPill>}
                            {isCurated && (
                              <GlassPill mono>
                                <span className="drills-card__curated-dot" />
                                Coach&apos;s Corner{d.curator_name ? ` · ${d.curator_name}` : ''}
                              </GlassPill>
                            )}
                          </div>
                        </div>
                        {d.description && <p className="drills-card__desc">{d.description}</p>}
                        {d.sub_themes.length > 0 && (
                          <div className="drills-card__themes">
                            {d.sub_themes.slice(0, 5).map((theme) => (
                              <span key={theme} className="drills-card__theme">{theme}</span>
                            ))}
                          </div>
                        )}
                        {/* Author + recency byline. Drives the "Authored
                            by" filter — coach sees who created what at a
                            glance. */}
                        <div style={{ fontSize: 11, opacity: 0.6, marginTop: 6, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <span>
                            {d.source_type === 'PLATFORM' || d.source_type === 'LICENSED'
                              ? 'Library'
                              : d.source_type === 'CURATED_EXPERT'
                              ? `Curated · ${d.curator_name ?? 'expert'}`
                              : d.source_type === 'AI_GENERATED'
                              ? 'Drafted by Brain'
                              : d.created_by === user?.id
                              ? 'You'
                              : d.author_name
                              ? `${d.author_name}`
                              : 'Coach-authored'}
                          </span>
                          {d.updated_at && (
                            <span>· updated {relativeTime(d.updated_at)}</span>
                          )}
                        </div>
                        {/* Card actions. Refine requires academy tier (a
                            curation affordance). Rename + Delete act on the
                            coach's own / Brain drafts only. Duplicate is
                            offered on every drill — cloning a library drill
                            into an editable coach copy is the headline use. */}
                        {user && (
                          <div className="drills-card__actions" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                            {showAuthoring && (
                              <button
                                type="button"
                                className="drills-card__refine"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  navigate(`/drills/new?refine=${d.id}`);
                                }}
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  color: 'inherit',
                                  font: 'inherit',
                                  cursor: 'pointer',
                                  padding: 0,
                                }}
                              >
                                <span aria-hidden="true">✨</span> Refine with Brain
                              </button>
                            )}
                            {canEdit && (
                              <button
                                type="button"
                                data-testid="drill-rename"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  const current = renamed[d.id] ?? d.name ?? '';
                                  setRenameValue(current);
                                  setRenameTarget({ id: d.id, name: current || 'this drill' });
                                }}
                                title="Rename this drill"
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  color: 'inherit',
                                  font: 'inherit',
                                  cursor: 'pointer',
                                  padding: 0,
                                  fontSize: 13,
                                }}
                              >
                                ✏️ Rename
                              </button>
                            )}
                            <button
                              type="button"
                              data-testid="drill-duplicate"
                              disabled={duplicatingId === d.id}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                void handleDuplicate(d);
                              }}
                              title="Duplicate this drill into an editable copy"
                              style={{
                                background: 'transparent',
                                border: 'none',
                                color: 'inherit',
                                font: 'inherit',
                                cursor: duplicatingId === d.id ? 'wait' : 'pointer',
                                padding: 0,
                                fontSize: 13,
                                opacity: duplicatingId === d.id ? 0.6 : 1,
                              }}
                            >
                              {duplicatingId === d.id ? 'Duplicating…' : '⧉ Duplicate'}
                            </button>
                            {canEdit && (
                              <button
                                type="button"
                                data-testid="drill-delete"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setDeleteTarget({ id: d.id, name: renamed[d.id] ?? d.name ?? 'this drill' });
                                }}
                                title="Delete this drill"
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  color: 'rgba(255, 100, 100, 0.85)',
                                  font: 'inherit',
                                  cursor: 'pointer',
                                  padding: 0,
                                  fontSize: 13,
                                }}
                              >
                                🗑 Delete
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      <p className="drills-footnote">
        <Link to="/sessions/new">Plan a session</Link> to compose drills into tonight, or open ⌘K and ask Brain.
      </p>

      {deleteTarget && (
        <div
          role="dialog"
          aria-label="Delete drill"
          style={{
            position: 'fixed', inset: 0, zIndex: 1100,
            background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(8px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={() => !deleting && setDeleteTarget(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(420px, calc(100vw - 32px))',
              background: 'rgba(15, 25, 45, 0.97)',
              border: '1px solid rgba(255,255,255,0.18)',
              borderRadius: 12,
              padding: 20,
              color: '#fff',
              fontFamily: 'system-ui, sans-serif',
              display: 'flex', flexDirection: 'column', gap: 12,
            }}
          >
            <h2 style={{ margin: 0, fontSize: 16 }}>Delete this drill?</h2>
            <p style={{ margin: 0, fontSize: 13, opacity: 0.85, lineHeight: 1.5 }}>
              <strong>{deleteTarget.name}</strong> will be removed from your library. The data is soft-deleted so it can be recovered from the database if needed.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                type="button"
                disabled={deleting}
                onClick={() => setDeleteTarget(null)}
                style={{
                  padding: '8px 14px',
                  background: 'transparent',
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: 6,
                  fontSize: 13,
                  cursor: deleting ? 'wait' : 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={deleting}
                onClick={async () => {
                  setDeleting(true);
                  const { error } = await supabase
                    .from('drills')
                    .update({ deleted_at: new Date().toISOString() })
                    .eq('id', deleteTarget.id);
                  setDeleting(false);
                  if (error) {
                    alert(`Couldn't delete drill: ${error.message}`);
                    return;
                  }
                  setDeletedIds((prev) => {
                    const next = new Set(prev);
                    next.add(deleteTarget.id);
                    return next;
                  });
                  setDeleteTarget(null);
                }}
                style={{
                  padding: '8px 14px',
                  background: 'var(--state-bad)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: deleting ? 'wait' : 'pointer',
                }}
              >
                {deleting ? 'Deleting…' : 'Delete drill'}
              </button>
            </div>
          </div>
        </div>
      )}

      {renameTarget && (
        <div
          role="dialog"
          aria-label="Rename drill"
          style={{
            position: 'fixed', inset: 0, zIndex: 1100,
            background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(8px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={() => !renaming && setRenameTarget(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(420px, calc(100vw - 32px))',
              background: 'rgba(15, 25, 45, 0.97)',
              border: '1px solid rgba(255,255,255,0.18)',
              borderRadius: 12,
              padding: 20,
              color: '#fff',
              fontFamily: 'system-ui, sans-serif',
              display: 'flex', flexDirection: 'column', gap: 12,
            }}
          >
            <h2 style={{ margin: 0, fontSize: 16 }}>Rename drill</h2>
            <input
              type="text"
              autoFocus
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); void submitRename(); }
                if (e.key === 'Escape' && !renaming) setRenameTarget(null);
              }}
              aria-label="New drill name"
              placeholder="Drill name"
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '10px 12px', fontSize: 14,
                background: 'rgba(255,255,255,0.08)', color: '#fff',
                border: '1px solid rgba(255,255,255,0.2)', borderRadius: 6,
              }}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                type="button"
                disabled={renaming}
                onClick={() => setRenameTarget(null)}
                style={{
                  padding: '8px 14px',
                  background: 'transparent',
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: 6,
                  fontSize: 13,
                  cursor: renaming ? 'wait' : 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={renaming || !renameValue.trim()}
                onClick={() => void submitRename()}
                style={{
                  padding: '8px 14px',
                  background: 'var(--accent, #2980B9)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  fontWeight: 600,
                  fontSize: 13,
                  opacity: renaming || !renameValue.trim() ? 0.6 : 1,
                  cursor: renaming || !renameValue.trim() ? 'not-allowed' : 'pointer',
                }}
              >
                {renaming ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
